import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  alpha,
  useTheme,
  Paper,
} from '@mui/material';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { statisticsService } from '../services/statisticsService';
const SizeTooltip = ({ active, payload }: any) => {
  const theme = useTheme();
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <Paper sx={{ p: 1.5, border: `1px solid ${alpha(theme.palette.divider, 0.2)}`, borderRadius: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>{data.formattedTime}</Typography>
        <Typography variant="body2" color="text.secondary">
          Size: {data.totalSize.toLocaleString()}
        </Typography>
      </Paper>
    );
  }
  return null;
};

const ChangesTooltip = ({ active, payload }: any) => {
  const theme = useTheme();
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <Paper sx={{ p: 1.5, border: `1px solid ${alpha(theme.palette.divider, 0.2)}`, borderRadius: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>{data.formattedTime}</Typography>
        <Typography variant="body2" sx={{ color: theme.palette.success.main }}>
          +{data.totalAdditions.toLocaleString()} added
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.error.main }}>
          -{data.totalRemovals.toLocaleString()} removed
        </Typography>
      </Paper>
    );
  }
  return null;
};

export const BlacklistHourlyChart: React.FC = () => {
  const theme = useTheme();
  const [timeRange, setTimeRange] = useState<number>(24);

  const { data, isLoading } = useQuery({
    queryKey: ['blacklistHourlyStats', timeRange],
    queryFn: () => statisticsService.getBlacklistHourlyStatistics(timeRange),
    refetchInterval: 300000,
  });

  const handleTimeRangeChange = (_: React.MouseEvent<HTMLElement>, newValue: number | null) => {
    if (newValue !== null) {
      setTimeRange(newValue);
    }
  };

  const chartData = React.useMemo(() => {
    if (!data?.dataPoints) return [];
    return data.dataPoints.map((point) => {
      const timestamp = parseISO(point.timestamp);
      let formattedTime: string;
      if (timeRange <= 6) {
        formattedTime = format(timestamp, 'HH:mm');
      } else if (timeRange <= 24) {
        formattedTime = format(timestamp, 'HH:mm');
      } else if (timeRange <= 168) {
        formattedTime = format(timestamp, 'MMM d HH:mm');
      } else {
        formattedTime = format(timestamp, 'MMM d');
      }
      return {
        ...point,
        formattedTime,
        // Negate removals so they go below the zero line
        negRemovals: -(point.totalRemovals),
      };
    });
  }, [data?.dataPoints, timeRange]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Blacklist Statistics
          </Typography>
          {data && (
            <Typography variant="body2" color="text.secondary">
              Size: {data.summary.currentSize.toLocaleString()} &bull;{' '}
              +{data.summary.totalAdditions.toLocaleString()} / -{data.summary.totalRemovals.toLocaleString()} &bull;{' '}
              Net: {data.summary.netChange >= 0 ? '+' : ''}{data.summary.netChange.toLocaleString()}
            </Typography>
          )}
        </Box>
        <ToggleButtonGroup
          value={timeRange}
          exclusive
          onChange={handleTimeRangeChange}
          size="small"
          sx={{ '& .MuiToggleButton-root': { px: 1.5, py: 0.25, fontSize: '0.75rem', textTransform: 'none' } }}
        >
          <ToggleButton value={6}>6h</ToggleButton>
          <ToggleButton value={24}>24h</ToggleButton>
          <ToggleButton value={72}>3d</ToggleButton>
          <ToggleButton value={168}>7d</ToggleButton>
          <ToggleButton value={720}>30d</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
          <Typography variant="body2" color="text.secondary">Loading...</Typography>
        </Box>
      ) : chartData.length === 0 ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
          <Typography variant="body2" color="text.secondary">No data available yet. Statistics are collected every 15 minutes.</Typography>
        </Box>
      ) : (
        <Box>
          {/* Total Size - Area Chart */}
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.08em', mb: 0.5 }}>
            Total Size
          </Typography>
          <Box sx={{ width: '100%', height: 180 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
                <defs>
                  <linearGradient id="sizeGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.15} />
                    <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} vertical={false} />
                <XAxis dataKey="formattedTime" hide />
                <YAxis
                  stroke={theme.palette.text.secondary}
                  style={{ fontSize: '0.7rem' }}
                  tickLine={false}
                  tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}
                  domain={['dataMin - 100', 'dataMax + 100']}
                />
                <Tooltip content={<SizeTooltip />} />
                <Area
                  type="monotone"
                  dataKey="totalSize"
                  stroke={theme.palette.primary.main}
                  strokeWidth={2}
                  fill="url(#sizeGradient)"
                  dot={false}
                  activeDot={{ r: 3, fill: theme.palette.primary.main }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </Box>

          {/* Additions / Removals - Diverging Bar Chart */}
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.08em', mt: 2, mb: 0.5 }}>
            Changes
          </Typography>
          <Box sx={{ width: '100%', height: 140 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 5, right: 5, left: -15, bottom: 5 }} stackOffset="sign">
                <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} vertical={false} />
                <XAxis
                  dataKey="formattedTime"
                  stroke={theme.palette.text.secondary}
                  style={{ fontSize: '0.7rem' }}
                  tickLine={false}
                  interval="preserveStartEnd"
                  minTickGap={50}
                />
                <YAxis
                  stroke={theme.palette.text.secondary}
                  style={{ fontSize: '0.7rem' }}
                  tickLine={false}
                />
                <Tooltip content={<ChangesTooltip />} />
                <ReferenceLine y={0} stroke={alpha(theme.palette.divider, 0.5)} />
                <Bar
                  dataKey="totalAdditions"
                  name="Additions"
                  fill={theme.palette.success.main}
                  fillOpacity={0.75}
                  radius={[2, 2, 0, 0]}
                  maxBarSize={20}
                />
                <Bar
                  dataKey="negRemovals"
                  name="Removals"
                  fill={theme.palette.error.main}
                  fillOpacity={0.75}
                  radius={[0, 0, 2, 2]}
                  maxBarSize={20}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      )}
    </Box>
  );
};
